import { Card, Col, Row } from "react-bootstrap";
import HippoTable from "components/ReactTable/HipposReactTable";
import { useContext, useEffect, useState } from "react";
import { apiService } from "service/ApiService";
import StoreContext from "contexts/StoreContext";
import ShowModalButton from "views/Components/ShowModalButton";
import TimestampColumn from "components/Custom/TimestampColumn";
import DocumentLinesModal from "components/Modals/DocumentLinesModal";
import ExportToExcelButton from "views/Components/ExportToExcelButton";
import OpenExternalLinkButton from "views/Components/OpenExternalLinkButton";
import { exportToExcel, serverTimesampToNormalDate } from "utils/AppUtils";
import { Tooltip } from "react-tooltip";
import DateRangeContext from "contexts/DateRangeContext";
import { ClipLoader } from "react-spinners";

import { useTranslation } from "react-i18next";

function DocumentsTable({ sendData }) {
  const {
    t,
  } = useTranslation();

  const { storeId, setStoreId } = useContext(StoreContext);
  const { dateRange, setDateRange } = useContext(DateRangeContext);
  const [data, setData] = useState([]);
  const [show, setShow] = useState(false);
  const [docId, setDocId] = useState(null);
  const [docOrder, setDocOrder] = useState(null);
  const [docNumber, setDocNumber] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const handleClose = () => setShow(false);
  const handleShow = (id, order, number) => {
    setDocId(id);
    setDocOrder(order);
    setDocNumber(number);
    setShow(true);
  };

  const columns = [
    {
      Header: "ID",
      accessor: "localId",
      maxWidth: 70,
      minWidth: 70,
      width: 60,
    },
    {
      Header: t("tables.headers.order"),
      accessor: "order",
      minWidth: 100,
      maxWidth: 300,
      width: 500,
    },
    {
      Header: t("tables.headers.number"),
      accessor: "number",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.mARK"),
      accessor: "mark",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.table"),
      accessor: "tableName",
      minWidth: 80,
      maxWidth: 80,
      width: 70,
    },
    {
      Header: t("tables.headers.customerVatNumber"),
      accessor: "customerVat",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.totalEuro"),
      accessor: "total",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.vatEuro"),
      accessor: "vat",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.payment"),
      accessor: "paymentMethod",
      minWidth: 100,
      maxWidth: 200,
      width: 100,
    },
    {
      Header: t("tables.headers.createdAt"),
      accessor: "localCreatedAt",
      minWidth: 200,
      maxWidth: 400,
      width: 500,
    },
    {
      Header: t("tables.headers.actions"),
      accessor: "actions",
      maxWidth: 130,
      minWidth: 90,
      width: 100,
    },
  ];

  const getData = () => {
    setShowLoader(true);
    apiService
      .post(`api/v1/documents/store/${storeId}`, {
        fromDate: dateRange[0],
        toDate: dateRange[1],
      })
      .then((resp) => {
        sendData(resp);
        setData(attachCustom(resp));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const attachCustom = (data) => {
    const enhanced = data.map((el) => {
      el.total = el.total.toFixed(2);
      el.vat = el.vat.toFixed(2);
      el.tableName = el.table ? el.table.name : "";
      el.paymentMethod = _.get(el, "paymentMethod.name", "");
      el.rowLocalCreatedAt = el.localCreatedAt;
      el.localCreatedAt = <TimestampColumn timestamp={el.localCreatedAt} />;
      el.actions = (
        <Row>
          <OpenExternalLinkButton title={t("common.labels.viewDocumentAtProvider")} url={el.qrUrl} />
          <ShowModalButton
            handler={() => handleShow(el.id, el.order, el.number)}
            variant="warning"
            icon="fa fa-list-ul"
            className="text-warning btn-link"
            title={t("common.actions.viewDocumentLines")}
          />
          {el.transmissionFailure === 1 ? (
            <div className="pr-1">
              <i
                data-tooltip-id="my-tooltip"
                data-tooltip-content={t("common.labels.unableToConnectBusinessAndProvider")}
                className="pt-2 float-right fa fa-exclamation text-danger"
              ></i>
              <Tooltip id="my-tooltip" />
            </div>
          ) : el.transmissionFailure === 2 ? (
            <div className="pr-1">
              <i
                data-tooltip-id="my-tooltip"
                data-tooltip-content={t("common.labels.unableToConnectProviderAndMydata")}
                className="pt-2 float-right fa fa-exclamation text-warning"
              ></i>
              <Tooltip id="my-tooltip" />
            </div>
          ) : null}
        </Row>
      );
      return el;
    });

    return enhanced;
  };

  const formatData = (data) => {
    if (data.length === 0) return [];
    let formattedData = data.map((row) => {
      return {
        ID: row.localId,
        [t("tables.headers.order")]: row.order,
        [t("tables.headers.number")]: row.number,
        [t("tables.headers.mARK")]: row.mark,
        [t("tables.headers.table")]: row.table ? row.table.name : null,
        [t("tables.headers.customerVatNumber")]: row.customerVat,
        [t("tables.headers.totalEuro")]: row.total,
        [t("tables.headers.vatEuro")]: row.vat,
        [t("tables.headers.payment")]: row.paymentMethod,
        [t("tables.headers.createdAt")]: serverTimesampToNormalDate(row.rowLocalCreatedAt),
      };
    });
    return formattedData;
  };

  const handleExportData = () => {
    setExcelLoading(true);
    exportToExcel(formatData(data), `invoices-${dateRange[0]}-to-${dateRange[1]}`, t("common.labels.documents"));
    setExcelLoading(false);
  };

  useEffect(() => {
    if (!storeId || !dateRange) return;
    getData();
  }, [storeId, dateRange]);

  return (
    <>
      <DocumentLinesModal docId={docId} docOrder={docOrder} docNumber={docNumber} show={show} handleClose={handleClose} />
      <Card className="strpied-tabled-with-hover">
        <Card.Header>
          <Row>
            <Col>
              <Card.Title as="h4">{t("common.labels.issuedDocuments")}</Card.Title>
            </Col>
          </Row>
          <Row>
            <Col>
              <p className="card-category">{t(
                "messages.showsDocumentsIssuedDuringThePeriodDefinedByTheSelectedDatesAndTimes",
              )}</p>
            </Col>
            <Col md="2">{data.length === 0 ? "" : <ExportToExcelButton handler={handleExportData} disabled={excelLoading} />}</Col>
          </Row>
        </Card.Header>
        <Card.Body className="table-responsive p-0">
          {showLoader ? (
            <div style={{ textAlign: "center", marginTop: "5vh", marginBottom: "5vh" }}>
              <ClipLoader size={50} color={"#ff6fbc"} />
            </div>
          ) : (
            <Col>
              <HippoTable data={data} columns={columns} className="-striped -highlight primary-pagination" />
            </Col>
          )}
        </Card.Body>
        <Card.Footer>
          <div className="mb-1"></div>
        </Card.Footer>
      </Card>
    </>
  );
}

export default DocumentsTable;
